import { genBlock } from "@/utils/bem";
import { Box, Flex, Image, Center, ChakraProps, chakra } from "@chakra-ui/react";
import { observer } from "mobx-react-lite";
import React, { useContext, useMemo } from "react";
import { WordEntityTypeWithParentKeys } from "../type";

import "./style.scss";
import classNames from "classnames";
import { EllipseText } from "../../styleComponents/Texts";
import { WordTableContext } from "../context";
import { langStore, translate } from "@noli/business";
import { Editor_Words } from "@/utils/lang/editor";
import { WithClassName } from "@/types/base";

export const block = genBlock("c-keyword-item-card");

/**
 * 关键词选项
 */
const IKeywordItemCard: React.FC<{
  data: WordEntityTypeWithParentKeys;
}> = ({ data }) => {
  const { onSelect, nameDict, category, checkValueFn } = useContext(WordTableContext);
  const lang = langStore.tgt;

  const isChecked = checkValueFn ? checkValueFn(data) : false;
  const imgUrl = useMemo(() => {
    if (data.image_urls) return data.image_urls;
    if (!data.img_urls) return null;
    return category ? data.img_urls?.[category] : Object.values(data.img_urls)?.[0];
  }, [data.img_urls, category]);

  return (
    <Flex
      flexDir="column"
      border="1px solid"
      borderColor="text.primary"
      h="100%"
      alignItems="center"
      rounded="4px"
      overflow="hidden"
      cursor="pointer"
      fontSize="12px"
      lineHeight="16px"
      className={classNames(block(), { checked: isChecked, hide_usage: !!data.img_urls })}
      onClick={() => onSelect(data)}
    >
      {imgUrl ? (
        <Box pos="relative" w="100%" flex={1}>
          <Image
            src={`${imgUrl}?x-oss-process=image/resize,w_250`}
            w="100%"
            h="100%"
            objectFit="cover"
          />
          <UsageOverlay isChecked={isChecked} needOverlay />
        </Box>
      ) : null}
      <Center w="100%" p="8px 6px 0px" flexDirection="column" h="48px" gap="4px">
        {lang === "zh" && (
          <EllipseText w="100%" color="text.secondary">
            <span title={nameDict[data.key]?.zh}>{nameDict[data.key]?.zh}</span>
          </EllipseText>
        )}
        <EllipseText w="100%" color="text.secondary" onClick={() => onSelect(data)}>
          <span title={nameDict[data.key]?.en}>{nameDict[data.key]?.en}</span>
        </EllipseText>
      </Center>
      {!imgUrl ? <UsageOverlay isChecked={isChecked} /> : null}
    </Flex>
  );
};

const IUsageOverlay: React.FC<WithClassName<{ isChecked: boolean; needOverlay?: boolean }>> = ({
  isChecked,
  needOverlay,
  className,
}) => {
  const lang = langStore.tgt;
  const overlayProps = useMemo(() => {
    if (needOverlay) {
      const style: ChakraProps = {
        pos: "absolute",
        h: "100%",
        top: 0,
        left: 0,
        pb: "10px",
        bg: "linear-gradient(180deg, rgba(0, 0, 0, 0) 53.65%, rgba(0, 0, 0, 0.5) 100%)",
        alignItems: "flex-end",
      };
      return style;
    }
    return {
      marginTop: "12px",
      pb: "6px",
    };
  }, [needOverlay]);
  return (
    <Flex
      className={classNames(className, block({ e: "overlay" }))}
      width="100%"
      justifyContent="center"
      {...overlayProps}
    >
      <Box
        color={isChecked ? "brand.primary" : "text.secondary"}
        border="1px solid"
        rounded="2px"
        p="2px 8px"
        borderColor={isChecked ? "brand.primary" : "text.secondary"}
      >
        {translate(Editor_Words[isChecked ? "using-keyword" : "use-keyword"], lang)}
      </Box>
    </Flex>
  );
};
export const UsageOverlay = chakra(observer(IUsageOverlay));
export const KeywordItemCard = observer(IKeywordItemCard);
